import React, { useState, useEffect } from 'react';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Loading } from '../components/Loading';
import { expenseService } from '../services/expenseService';
import { Expense, CreateExpenseData } from '../types';
import { formatCurrency } from '../utils/currency';
import { format, parseISO } from 'date-fns';
import { showToast } from '../utils/toast';

export const Expenses: React.FC = () => {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [filters, setFilters] = useState({
    date: '',
    month: '',
    year: new Date().getFullYear().toString(),
    type: '',
    start_date: '',
    end_date: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [formData, setFormData] = useState<CreateExpenseData>({
    name: '',
    description: '',
    expense_type: '',
    amount: 0,
    date: format(new Date(), 'yyyy-MM-dd'),
  });

  useEffect(() => {
    loadExpenses();
  }, [filters]);

  const loadExpenses = async () => {
    try {
      setLoading(true);

      const params: any = {};
      if (filters.date) params.date = filters.date;
      if (filters.month) params.month = filters.month;
      if (filters.year) params.year = filters.year;
      if (filters.type) params.type = filters.type;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;

      const response = await expenseService.getExpenses(params);
      if (response.success && response.data) {
        setExpenses(response.data);
      } else {
        showToast.error(response.message || 'Failed to load expenses');
      }
    } catch (err: any) {
      showToast.error(err.response?.data?.message || err.message || 'Failed to load expenses');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingExpense) {
        const response = await expenseService.updateExpense(editingExpense.id, formData);
        if (response.success) {
          showToast.success('Expense updated successfully!');
          resetForm();
          loadExpenses();
        } else {
          showToast.error(response.message || 'Failed to update expense');
        }
      } else {
        const response = await expenseService.createExpense(formData);
        if (response.success) {
          showToast.success('Expense created successfully!');
          resetForm();
          loadExpenses();
        } else {
          showToast.error(response.message || 'Failed to create expense');
        }
      }
    } catch (err: any) {
      showToast.error(err.response?.data?.message || err.message || 'Failed to save expense');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (expense: Expense) => {
    setEditingExpense(expense);
    setFormData({
      name: expense.name,
      description: expense.description || '',
      expense_type: expense.expense_type,
      amount: expense.amount,
      date: expense.date,
    });
    setShowForm(true);
  };

  const handleDelete = async (expenseId: number) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) {
      return;
    }

    try {
      const response = await expenseService.deleteExpense(expenseId);
      if (response.success) {
        showToast.success('Expense deleted successfully!');
        loadExpenses();
      } else {
        showToast.error(response.message || 'Failed to delete expense');
      }
    } catch (err: any) {
      showToast.error(err.response?.data?.message || err.message || 'Failed to delete expense');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      expense_type: '',
      amount: 0,
      date: format(new Date(), 'yyyy-MM-dd'),
    });
    setEditingExpense(null);
    setShowForm(false);
  };

  const clearFilters = () => {
    setFilters({
      date: '',
      month: '',
      year: new Date().getFullYear().toString(),
      type: '',
      start_date: '',
      end_date: '',
    });
  };

  const totalAmount = expenses.reduce((sum, expense) => sum + expense.amount, 0);

  const handleExportExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.type) params.append('type', filters.type);

      const token = localStorage.getItem('access_token');
      const url = `/api/expenses/export/excel${params.toString() ? `?${params.toString()}` : ''}`;

      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000';
      const response = await fetch(`${baseUrl}${url}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `expenses_export_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        showToast.success('Expenses exported to Excel successfully!');
      } else {
        const data = await response.json();
        showToast.error(data.message || 'Failed to export expenses');
      }
    } catch (err: any) {
      showToast.error(err.message || 'Failed to export expenses');
    }
  };

  const handleExportPDF = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.type) params.append('type', filters.type);

      const token = localStorage.getItem('access_token');
      const url = `/api/expenses/export/pdf${params.toString() ? `?${params.toString()}` : ''}`;

      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000';
      const response = await fetch(`${baseUrl}${url}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `expenses_export_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        showToast.success('Expenses exported to PDF successfully!');
      } else {
        const data = await response.json();
        showToast.error(data.message || 'Failed to export expenses');
      }
    } catch (err: any) {
      showToast.error(err.message || 'Failed to export expenses');
    }
  };

  if (loading && expenses.length === 0) {
    return <Loading fullScreen text="Loading expenses..." />;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Expenses Management</h1>
        <div className="flex space-x-3">
          <Button
            variant="secondary"
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </Button>
          <div className="flex space-x-2 border-r pr-3 mr-3">
            <Button
              variant="secondary"
              onClick={handleExportExcel}
              title="Export to Excel"
            >
              📊 Excel
            </Button>
            <Button
              variant="secondary"
              onClick={handleExportPDF}
              title="Export to PDF"
            >
              📄 PDF
            </Button>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
          >
            + Add Expense
          </Button>
        </div>
      </div>


      {/* Filters */}
      {showFilters && (
        <Card title="Filters">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Date"
              type="date"
              value={filters.date}
              onChange={(e) => setFilters({ ...filters, date: e.target.value })}
            />
            <Input
              label="Month"
              type="number"
              min="1"
              max="12"
              value={filters.month}
              onChange={(e) => setFilters({ ...filters, month: e.target.value })}
              placeholder="1-12"
            />
            <Input
              label="Year"
              type="number"
              value={filters.year}
              onChange={(e) => setFilters({ ...filters, year: e.target.value })}
            />
            <Input
              label="Type (Search)"
              type="text"
              value={filters.type}
              onChange={(e) => setFilters({ ...filters, type: e.target.value })}
              placeholder="Filter by expense type"
            />
            <Input
              label="Start Date"
              type="date"
              value={filters.start_date}
              onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
            />
            <Input
              label="End Date"
              type="date"
              value={filters.end_date}
              onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
            />
          </div>
          <div className="mt-4 flex space-x-3">
            <Button variant="secondary" onClick={clearFilters}>
              Clear Filters
            </Button>
            <Button onClick={loadExpenses}>
              Apply Filters
            </Button>
          </div>
        </Card>
      )}

      {/* Expense Form */}
      {showForm && (
        <Card title={editingExpense ? "Edit Expense" : "Add New Expense"}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Expense Name *"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="e.g., Fuel for delivery"
              />
              <Input
                label="Expense Type *"
                type="text"
                value={formData.expense_type}
                onChange={(e) => setFormData({ ...formData, expense_type: e.target.value })}
                required
                placeholder="e.g., Fuel, Maintenance, Transport"
              />
              <Input
                label="Amount *"
                type="number"
                step="0.01"
                min="0.01"
                value={formData.amount || ''}
                onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                required
                placeholder="0.00"
              />
              <Input
                label="Date *"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Additional details about the expense..."
              />
            </div>
            <div className="flex space-x-3">
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Saving...' : editingExpense ? 'Update Expense' : 'Create Expense'}
              </Button>
              <Button type="button" variant="secondary" onClick={resetForm}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Expenses Summary */}
      <Card title="Expenses Summary">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">Total Expenses</p>
            <p className="text-2xl font-bold text-blue-600">{formatCurrency(totalAmount)}</p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">Number of Expenses</p>
            <p className="text-2xl font-bold text-green-600">{expenses.length}</p>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <p className="text-sm text-gray-600">Average per Expense</p>
            <p className="text-2xl font-bold text-purple-600">
              {expenses.length > 0 ? formatCurrency(totalAmount / expenses.length) : formatCurrency(0)}
            </p>
          </div>
        </div>
      </Card>

      {/* Expenses List */}
      <Card title="All Expenses">
        {expenses.length === 0 ? (
          <p className="text-gray-500 text-center py-8">
            No expenses found. {!showForm && 'Create your first expense above.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {expenses.map((expense) => (
                  <tr key={expense.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {format(parseISO(expense.date), 'MMM dd, yyyy')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {expense.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-orange-100 text-orange-800">
                        {expense.expense_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-red-600">
                      {formatCurrency(expense.amount)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                      {expense.description || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleEdit(expense)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDelete(expense.id)}
                        >
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td colSpan={3} className="px-6 py-4 text-sm font-semibold text-gray-900 text-right">
                    Total:
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-red-600">
                    {formatCurrency(totalAmount)}
                  </td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

